import {useMemo, useState} from "react";
import {CompanyEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import { getFormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseCompanyDataTableFilterProps = Omit<UseDataTableFilterProps<CompanyEntity>, 'filterConfig'>

export function useCompanyDataTableFilter(props: UseCompanyDataTableFilterProps): UseDataTableFilterResult<CompanyEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [typeQuery, setTypeQuery] = useState<string | undefined>(undefined);
  const [typeDebouncedQuery] = useDebouncedValue(typeQuery, 200);

  const filterConfig: DataTableFilterConfig<CompanyEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: ({name}, query) => !name || !name.toLowerCase().includes(query.toLowerCase())
      },
      "type": {
        setQuery: setTypeQuery,
        query: typeQuery,
        debouncedQuery: typeDebouncedQuery,
        filter: ({type}, query) => {
          if (type) {
            const display = getFormattedDatabaseCode(type, t)
            return !type.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    typeQuery,
    typeDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
